import React, { Component, ReactNode } from 'react';
import { CommonConnectionModel, ConnectionModel } from '../../domain/connection/ConnectionDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import LanguageUtil from '../../util/LanguageUtil';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import ItemStatusComponent from '../ItemStatusComponent';
import ConnectionService from '../../service/ConnectionService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { ConnectionState } from '../../redux/reducers/ConnectionReducer';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;

class ConnectionListViewerComponent extends Component<Props> {
  private connectionService: ConnectionService = ServiceContainer.getInstance().get(ServiceNames.CONNECTION);

  public render(): ReactNode {
    const {
      views,
      connections,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && connections && views.length > 0 && connections.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={connections}
          columns={this.getColumns()}
          selectionMode={'checkbox'}
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          onRowClick={this.openEditor}
          componentName={ComponentName.CONNECTION}
          openNewTab={true}
          onSortChange={onSortChange}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          data-testid={`${ComponentName.CONNECTION}_list`}
        />
        <ListDraggableItem itemType={ComponentName.CONNECTION} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.CONNECTION} />
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: `pages.connection.table.name`, defaultMessage: 'Name' }),
        className: 'min-w-[18%]',
        copyable: true,
        onCopy: (e: CommonConnectionModel) => {
          return e.name;
        },
        body: (record: CommonConnectionModel) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          if (view == undefined) return null;
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={30} text={record.name} data-testid={`${ComponentName.CONNECTION}_name_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: `pages.connection.table.status`, defaultMessage: 'Status' }),
        body: (connection: CommonConnectionModel) => {
          return (
            <ItemStatusComponent
              status={connection.stage || LanguageUtil.get({ id: `common.status.draft`, defaultMessage: 'DRAFT' })}
              data-testid={`${ComponentName.CONNECTION}_stage_text_${connection.name}`}
            />
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: `pages.connection.table.description`, defaultMessage: 'Description' }),
        className: 'min-w-[16%]',
        body: (record: CommonConnectionModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 justify-start relative group'}>
              <CustomTableContentWrapper
                clipSize={35}
                text={
                  record.description ||
                  LanguageUtil.get({
                    id: `pages.connection.table.noDescription`,
                    defaultMessage: 'This connection does not have a description yet.',
                  })
                }
                data-testid={`${ComponentName.CONNECTION}_description_text_${record.name}`}
              />
            </div>
          );
        },
      },
      {
        field: 'databaseType',
        header: LanguageUtil.get({ id: `pages.connection.table.type`, defaultMessage: 'Type' }),
        className: 'w-1/6',
        body: (record: CommonConnectionModel) => {
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <CustomTableContentWrapper text={record.databaseType} data-testid={`${ComponentName.CONNECTION}_database_type_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: `pages.connection.table.createdAt`, defaultMessage: 'CreatedAt' }),
        className: 'w-1/6',
        body: (record: CommonConnectionModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: `pages.connection.table.updatedAt`, defaultMessage: 'updatedAt' }),
        className: 'w-1/6',
        body: (record: CommonConnectionModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Connection')) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: `pages.connection.table.action`, defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: CommonConnectionModel) => {
    const { permissions } = this.props;

    const actions: ReactNode[] = [];

    const permission = permissions[record.name];

    const allowed = ['Edit', 'Admin'];
    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.CONNECTION, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.CONNECTION, [record.name], () => this.connectionService.undeployConnection(record)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
          data-testid={`${ComponentName.CONNECTION}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.connectionService.removeConnection(record.name),
              message: LanguageUtil.get({ id: 'messages.confirm.confirmRemove', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.CONNECTION}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.connectionService.deployConnection(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.CONNECTION}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );

    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openConnection } = this.props;
    if (e.data) {
      openConnection(e.data.name);
    }
  };
}

const mapStateToProps = (state: RootState) => ({
  connection: state.connection as ConnectionState,
  component: state.component as ComponentState,
});

const mapDispatchToProps = {};

interface Props extends ReduxProps {
  views: any;
  connections: ConnectionModel[];
  openConnection: (id: string) => void;
  currentModal: any;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(ConnectionListViewerComponent);
